import { apiErrorMessage } from '@agos/api-client'
import {
  getGetApkBuildsQueryKey,
  getGetApkInfoQueryKey,
  useGetApkBuildMode,
  useGetApkBuilds,
  useGetApkInfo,
  postApkUploadDirect,
  usePostApkBuild,
} from '@agos/api-client/devices'
import { T, formatDateTime, tt, useAuth } from '@agos/shared'
import {
  BUTTON_PRIMARY,
  BUTTON_SECONDARY,
  cn,
  ConfirmDialog,
  EmptyState,
  PageHeader,
  Spinner,
  StatusBadge,
  useToast,
} from '@agos/ui'
import { useQueryClient } from '@tanstack/react-query'
import { AlertTriangle, Hammer, Info, RefreshCw, Smartphone, Upload } from 'lucide-react'
import { useRef, useState } from 'react'

import { mayWrite, readOnlyReason } from '../write-access'
import {
  BUILD_POLL_MS,
  activeBuild,
  pollExpired,
  pollGaveUpNote,
  apkInfoOf,
  buildStatusLabel,
  buildsOf,
  formatSize,
  historyNote,
  mayBuild,
  modeOf,
  readinessMessage,
  readinessOf,
  retirementNote,
  uploadProblem,
  uploadProblemMessage,
} from './apk-distribution'

/**
 * `/mobile-app` — the driver APK, and the build button behind it.
 *
 * **§9.6 supersedes this screen** and its removal is the owner's to sign off, so it is ported and says both.
 *
 * **FB-164.** `GET /apk/build/mode` reports `driverAppPresent: false` on this deployment — the driver-app
 * checkout is not mounted, so `eas build` has nothing to build. Legacy's `BuildMode` interface declares
 * `{mode, hasExpoToken, note}`: it ignores `driverAppPresent`, the field that answers *can this button work*,
 * and reads `note`, which the backend does not send. The button looked ready either way.
 *
 * **FB-165.** `POST /apk/upload-url` answers `/api/apk/upload-direct` and the endpoint is mounted at
 * `/api/v1/apk/upload-direct`. This page posts to the path that exists and the mismatch is filed.
 *
 * Ported from legacy `pages/mobile-app.tsx` (554 lines).
 */

export function MobileAppPage() {
  const { toast } = useToast()
  const { user } = useAuth()
  const queryClient = useQueryClient()

  const fileInput = useRef<HTMLInputElement>(null)
  const [uploading, setUploading] = useState(false)
  const [confirmBuild, setConfirmBuild] = useState(false)
  const [openLog, setOpenLog] = useState<string | null>(null)

  const info = useGetApkInfo({ query: { retry: false } })
  const mode = useGetApkBuildMode()
  const builds = useGetApkBuilds({
    query: {
      // Legacy polls every 3 s from an effect keyed on the build id. Here the query polls only while a build
      // is unfinished, so nothing is asked once there is nothing to watch.
      // §9.35 — and it stops. The interval used to depend only on *is a build unfinished*, so a
      // build stuck in a remote queue was asked about every three seconds for as long as the tab was
      // open. `pollExpired` reads the build's own age, so the decision needs no counter and therefore
      // no state written while deciding — which is what span the AI-translate poll to its cap.
      refetchInterval: (query) => {
        const build = activeBuild(buildsOf(query.state.data))
        if (build === null || pollExpired(build, Date.now())) return false
        return BUILD_POLL_MS
      },
    },
  })

  // `/apk/*` is one of the four sections an `admin` may write as well as read.
  const writable = mayWrite('apk', user?.systemRole)
  const readOnly = readOnlyReason('apk', user?.systemRole)

  const apk = apkInfoOf(info.data)
  const buildMode = modeOf(mode.data)
  const history = buildsOf(builds.data)
  const active = activeBuild(history)
  const readiness = readinessOf(buildMode)

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: getGetApkInfoQueryKey() })
    void queryClient.invalidateQueries({ queryKey: getGetApkBuildsQueryKey() })
  }

  const build = usePostApkBuild({
    mutation: {
      onSuccess: () => {
        refresh()
        toast({
          title: tt('Zostavovanie spustené'),
          description: tt('Priebeh sa objaví v zozname nižšie.'),
        })
      },
      onError: (caught) =>
        toast({
          variant: 'destructive',
          title: tt('Zostavovanie sa nespustilo'),
          description: apiErrorMessage(caught, tt('Server ho odmietol.')),
        }),
    },
  })

  const upload = async (file: File) => {
    const problem = uploadProblem({ name: file.name, size: file.size })
    if (problem !== null) {
      toast({ variant: 'destructive', title: uploadProblemMessage(problem) })
      return
    }
    setUploading(true)
    try {
      // Through the generated hook (§6). It used to go through `customFetch` because the spec typed
      // this request body as a string, so orval did `body: JSON.stringify(blob)` and posted `{}` — a
      // client that compiled, ran, and uploaded nothing. The body is declared `format: binary` now, so
      // the generated function takes a `Blob` and sets the media type itself.
      await postApkUploadDirect(file)
      refresh()
      toast({ title: tt('APK nahraté'), description: file.name })
    } catch {
      toast({
        variant: 'destructive',
        title: tt('Nahrávanie zlyhalo'),
        description: tt('Na serveri sa nič nezmenilo.'),
      })
    } finally {
      setUploading(false)
      if (fileInput.current !== null) fileInput.current.value = ''
    }
  }

  return (
    <div className="space-y-5">
      <PageHeader
        title={tt('Šoférska aplikácia')}
        description={tt('APK na stiahnutie a jeho zostavovanie')}
        icon={<Smartphone className="w-5 h-5" />}
        actions={
          <button
            type="button"
            data-refresh
            disabled={builds.isFetching}
            onClick={refresh}
            className={cn(BUTTON_SECONDARY, 'flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm')}
          >
            <RefreshCw className={cn('w-4 h-4', builds.isFetching && 'animate-spin')} /> <T>Obnoviť</T>
          </button>
        }
      />

      {readOnly !== null && (
        <p className="text-[11px] text-amber-500" data-read-only>
          {readOnly}
        </p>
      )}

      {/* §9.6, and that the removal is not this port's decision. */}
      <p className="text-xs text-muted-foreground flex items-start gap-2" data-retirement>
        <Info className="w-3.5 h-3.5 shrink-0 mt-0.5" />
        {retirementNote()}
      </p>

      <div className="bg-card border border-border rounded-2xl p-5 space-y-3" data-apk={apk === null ? 'none' : 'present'}>
        <h2 className="text-xs font-bold text-muted-foreground uppercase tracking-wider">
          <T>Nahraté APK</T>
        </h2>
        {info.isLoading ? (
          <Spinner />
        ) : apk === null ? (
          <p className="text-sm text-muted-foreground" data-no-apk>
            <T>Žiadne APK nie je nahraté - šofér si nemá čo stiahnuť.</T>
          </p>
        ) : (
          <div className="text-sm space-y-1">
            <p className="font-mono" data-apk-name>
              {apk.filename}
            </p>
            <p className="text-xs text-muted-foreground">
              <span data-apk-size>{formatSize(apk.sizeBytes)}</span>
              {apk.uploadedAt !== '' && ` · ${formatDateTime(apk.uploadedAt)}`}
            </p>
          </div>
        )}

        <div className="flex flex-wrap gap-3 pt-1">
          <input
            ref={fileInput}
            type="file"
            accept=".apk"
            aria-label={tt('Vybrať APK')}
            data-file
            disabled={!writable || uploading}
            onChange={(event) => {
              const file = event.target.files?.[0]
              if (file !== undefined) void upload(file)
            }}
            className="text-xs"
          />
          {uploading && <Spinner />}
        </div>
        <p className="text-[11px] text-muted-foreground flex items-center gap-1.5" data-upload-note>
          <Upload className="w-3 h-3 shrink-0" />
          <T>Nahráva sa priamo na server - nový súbor prepíše ten predchádzajúci.</T>
        </p>
      </div>

      <div className="bg-card border border-border rounded-2xl p-5 space-y-3">
        <h2 className="text-xs font-bold text-muted-foreground uppercase tracking-wider">
          <T>Zostavenie z kódu</T>
        </h2>
        {/* FB-164. Whether the button can work, from the field legacy ignored. */}
        <p
          className={cn(
            'text-xs flex items-start gap-2',
            readiness === 'no-source' || readiness === 'unknown' ? 'text-amber-500' : 'text-muted-foreground',
          )}
          data-readiness={readiness}
        >
          <AlertTriangle
            className={cn(
              'w-3.5 h-3.5 shrink-0 mt-0.5',
              readiness === 'local' || readiness === 'remote' ? 'opacity-0' : '',
            )}
          />
          {readinessMessage(readiness, buildMode)}
        </p>
        <button
          type="button"
          data-build
          disabled={!writable || build.isPending || !mayBuild(readiness) || active !== null}
          onClick={() => setConfirmBuild(true)}
          className={cn('flex items-center gap-2 px-4 py-2 rounded-xl', BUTTON_PRIMARY, 'text-sm')}
        >
          {build.isPending ? <Spinner /> : <Hammer className="w-4 h-4" />} <T>Zostaviť teraz</T>
        </button>
        {active !== null && (
          <p className="text-[11px] text-muted-foreground" data-active-build={active.id}>
            <T>Jeden beh už prebieha</T> - {buildStatusLabel(active.status)}.
          </p>
        )}
        {/* §9.35 - the watch stopping is worth saying. A page that quietly stops asking looks the
          * same as one whose build is still moving, and the difference is whether refreshing will
          * tell you anything. */}
        {/* `builds.dataUpdatedAt`, not `Date.now()`. The clock is impure during render - the compiler
          * refuses it, and it would not re-render as time passed anyway. The query's own timestamp is
          * a value, and it advances on exactly the answer that can change this. */}
        {active !== null && pollExpired(active, builds.dataUpdatedAt) && (
          <p className="text-[11px] text-amber-500" data-poll-gave-up={active.id}>
            {pollGaveUpNote()}
          </p>
        )}
      </div>

      <div className="bg-card border border-border rounded-2xl overflow-hidden">
        <div className="px-5 py-4 border-b border-border">
          <h2 className="text-xs font-bold text-muted-foreground uppercase tracking-wider">
            <T>Posledné behy</T>
          </h2>
          <p className="text-[11px] text-muted-foreground mt-1" data-history-note>
            {historyNote(history)}
          </p>
        </div>
        {history.length === 0 ? (
          <EmptyState
            icon={<Hammer className="w-10 h-10" />}
            title={tt('Žiadny beh')}
            description={tt('Zostavovanie ešte nebežalo, alebo sa server odvtedy restartoval.')}
          />
        ) : (
          <div className="divide-y divide-border">
            {history.map((record) => (
              <div key={record.id} className="px-5 py-3" data-build-row={record.id} data-build-status={record.status}>
                <div className="flex items-center gap-3">
                  <span className="font-mono text-xs text-muted-foreground">{record.id.slice(0, 8)}</span>
                  <StatusBadge
                    label={buildStatusLabel(record.status)}
                    color={
                      record.status === 'finished'
                        ? 'emerald'
                        : record.status === 'errored'
                          ? 'red'
                          : 'amber'
                    }
                  />
                  <span className="text-xs text-muted-foreground">
                    {record.startedAt === '' ? '-' : formatDateTime(record.startedAt)}
                  </span>
                  <button
                    type="button"
                    data-toggle-log={record.id}
                    onClick={() => setOpenLog((current) => (current === record.id ? null : record.id))}
                    className="ml-auto text-xs text-primary underline"
                  >
                    {openLog === record.id ? <T>Skryť výpis</T> : <T>Výpis</T>}
                  </button>
                </div>
                {record.error !== null && (
                  <p className="text-[11px] text-red-500 mt-1" data-build-error={record.id}>
                    {record.error}
                  </p>
                )}
                {openLog === record.id && (
                  <pre
                    className="mt-2 max-h-64 overflow-auto rounded-lg bg-background border border-border p-3 text-[11px] font-mono whitespace-pre-wrap"
                    data-build-log={record.id}
                  >
                    {record.log === '' ? tt('Výpis je prázdny.') : record.log}
                  </pre>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      <ConfirmDialog
        open={confirmBuild}
        onClose={() => setConfirmBuild(false)}
        title={tt('Zostaviť aplikáciu?')}
        description={`${readinessMessage(readiness, buildMode)} ${tt('Beh sa nedá zastaviť.')}`}
        confirmLabel={tt('Zostaviť')}
        icon={<Hammer className="w-5 h-5" />}
        loading={build.isPending}
        onConfirm={() => {
          setConfirmBuild(false)
          build.mutate({ data: {} })
        }}
      />
    </div>
  )
}
